import { Check, Minus, PencilSimpleLine, Plus, X } from "@phosphor-icons/react";
import { useState } from "react";

import { Button } from "common/components/ui/Button";
import Progress from "common/components/ui/Progress";
import {
  Table,
  TableCell,
  TableHead,
  TableRow,
} from "common/components/ui/TableV2";
import WorkspaceImage from "common/components/WorkspaceImage";
import useLicenseMutations from "common/datahooks/useLicenseMutations";
import usePromotions from "common/datahooks/usePromotions";
import useLicensedAccounts from "common/hooks/useLicensedAccounts";
import useNumberSpinner from "common/hooks/useNumberSpinner";
import type { Workspace } from "common/types";

const plusMinusButtonClassname =
  "p-2 disabled:text-black-400 hover:bg-black-200 rounded-xl transition-[background]";

function WorkspacesTableRow({
  workspace,
  isEditing,
  toggleIsEditing,
}: {
  workspace: Workspace;
  isEditing: boolean;
  toggleIsEditing: () => void;
}) {
  const { id, agency_license_count, name, icon_url } = workspace;
  const [seatCount, setSeatCount] = useState(agency_license_count);

  const { agencyPlan } = usePromotions();
  const { setLifetimeLicenseCount, isSettingLifetimeLicenseCount } =
    useLicenseMutations();

  const { inputProps, incrementButtonProps, decrementButtonProps } =
    useNumberSpinner(
      seatCount,
      setSeatCount,
      0,
      agency_license_count + agencyPlan.unassigned_license_count,
    );
  const { agencyLicensedAccounts } = useLicensedAccounts(id);

  return (
    <TableRow key={id}>
      <TableCell>
        <div className="flex gap-x-2.5 items-center">
          <WorkspaceImage id={id} imageSrc={icon_url} />
          <span className="text-md-bold">{name}</span>
        </div>
      </TableCell>

      <TableCell className="text-xs-bold">
        {agencyLicensedAccounts.length}
      </TableCell>

      <TableCell className="text-button-xs">
        <Progress
          value={(agencyLicensedAccounts.length / agency_license_count) * 100}
          className="w-16"
        />
      </TableCell>

      <TableCell className="w-32">
        {isEditing ? (
          <div className="flex bg-black-100 rounded-xl items-center text-black-950 w-fit transition-[background]">
            <button
              type="button"
              className={plusMinusButtonClassname}
              {...decrementButtonProps}
            >
              <Minus className="size-4" />
            </button>
            <input
              className="w-10 h-7 bg-white rounded-lg p-2 text-center text-button-xs focus-visible:border-purple-500 border focus-visible:outline-none border-white"
              {...inputProps}
            />
            <button
              type="button"
              className={plusMinusButtonClassname}
              {...incrementButtonProps}
            >
              <Plus className="size-4" />
            </button>
          </div>
        ) : (
          <span className="text-button-xs text-black-500">
            {agency_license_count}
          </span>
        )}
      </TableCell>

      <TableCell>
        {isEditing ? (
          <div className="flex gap-x-2 justify-end">
            <Button
              variant="secondary-black"
              size="sm"
              intent="iconOnly"
              disabled={isSettingLifetimeLicenseCount}
              onClick={() => {
                toggleIsEditing();
                //reset state on cancel
                setSeatCount(agency_license_count);
              }}
            >
              <X />
            </Button>
            <Button
              variant="secondary-purple"
              size="sm"
              intent="iconOnly"
              isLoading={isSettingLifetimeLicenseCount}
              onClick={() => {
                setLifetimeLicenseCount({
                  count: seatCount,
                  accountsToCancel: [],
                }).then(() => {
                  toggleIsEditing();
                });
              }}
            >
              <Check />
            </Button>
          </div>
        ) : (
          <Button
            variant="ghost-black"
            size="sm"
            intent="iconOnly"
            onClick={toggleIsEditing}
          >
            <PencilSimpleLine />
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}

export default function DesktopTable({
  workspaces,
  editingWorkspaceId,
  setEditingWorkspaceId,
}: {
  workspaces: Workspace[];
  editingWorkspaceId: string;
  setEditingWorkspaceId: (id: string) => void;
}) {
  return (
    <Table>
      <thead>
        <tr>
          <TableHead>Name</TableHead>
          <TableHead>Accounts</TableHead>
          <TableHead />
          <TableHead>Seats</TableHead>
          <TableHead className="w-28" />
        </tr>
      </thead>

      <tbody>
        {workspaces.map((workspace) => {
          const { id } = workspace;
          const isEditing = editingWorkspaceId === id;

          return (
            <WorkspacesTableRow
              key={id}
              workspace={workspace}
              isEditing={isEditing}
              toggleIsEditing={() => {
                setEditingWorkspaceId(isEditing ? null : id);
              }}
            />
          );
        })}
      </tbody>
    </Table>
  );
}
